<script setup>
import { t, countLabel, chapterTitle, message, schemaText } from '../lib/i18n.mjs'
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { zoom, zoomIdentity, select } from 'd3'
import { layoutDocument, edgeGeometry } from '../lib/layout.mjs'
import { unitText } from '../lib/document.mjs'
import AppIcon from './AppIcon.vue'
import NarrativePlayer from './NarrativePlayer.vue'
import MotifGallery from './MotifGallery.vue'
import FacetHatchPattern from './FacetHatchPattern.vue'
import { traceChain, placeLabels } from '../lib/focus.mjs'
const props = defineProps({
  document: Object,
  schemas: Array,
  motifs: Array,
  links: Array,
  selection: Object,
  filterIds: Object,
  stripeIds: Object,
  facetFilterIds: Object,
  collapsed: Boolean,
  busy: Boolean,
  analysisFocus: Object,
  focusId: String,
  hiddenNodeTypes: Object,
  hiddenLinkTypes: Object,
})
const emit = defineEmits(['select', 'annotate', 'toggle-panel', 'add-relation', 'focus'])
const viewMode = ref('graph')
const svg = ref(null),
  surface = ref(null),
  expanded = ref(new Set()),
  layout = ref(null),
  error = ref('')
const transform = ref(''),
  zoomLevel = ref(100),
  showParagraphs = ref(true),
  showUnits = ref(true)
let zoomBehavior,
  observer,
  dragState,
  dragged = false
let positions = {}
const camera = ref({ x: 0, y: 0, k: 1 })
const viewport = ref({ width: 0, height: 0 })
const allExpanded = computed(() => {
  const paragraphs = props.document.paragraphs.filter((p) => p.kind === 'paragraph')
  return paragraphs.length > 0 && paragraphs.every((p) => expanded.value.has(p.id))
})
const colors = computed(() => Object.fromEntries(props.schemas.map((s) => [s.id, s.color])))
function paragraphSummary(node) {
  const paragraph = props.document.paragraphs.find((p) => p.id === node.id)
  return (
    paragraph?.paragraph_representation ||
    paragraph?.argument_representation ||
    paragraph?.display_label ||
    t('尚无段落摘要')
  )
}
const linkTypes = computed(() => Object.fromEntries(props.links.map((s) => [s.id, s])))
const nodeVisible = (node) => !props.hiddenNodeTypes?.has(node.type)
const filteredGraph = computed(() => {
  const paragraphs = props.document.paragraphs.filter(nodeVisible)
  const units = props.document.units.filter(nodeVisible)
  const ids = new Set([...paragraphs, ...units].map((node) => node.id))
  const edges = props.document.edges.filter(
    (edge) => !props.hiddenLinkTypes?.has(edge.type) && ids.has(edge.source) && ids.has(edge.target),
  )
  return { ...props.document, paragraphs, units, edges }
})
const visibleNodes = computed(() => layout.value?.nodes.filter(nodeVisible) || [])
const visibleUnits = computed(() => layout.value?.units.filter(nodeVisible) || [])
const visibleEdges = computed(() => {
  if (!layout.value) return []
  const lookup = new Map([...layout.value.nodes, ...layout.value.units].map((n) => [n.id, n]))
  return filteredGraph.value.edges
    .filter(
      (e) =>
        (e.level === 'paragraph' ? showParagraphs.value : showUnits.value) &&
        lookup.has(e.source) &&
        lookup.has(e.target),
    )
    .map((e) => ({ ...e, ...edgeGeometry(lookup.get(e.source), lookup.get(e.target)) }))
})
const chain = computed(() =>
  props.analysisFocus
    ? { nodeIds: new Set(props.analysisFocus.nodeIds), edgeIds: new Set(props.analysisFocus.edgeIds) }
    : traceChain(filteredGraph.value, props.selection),
)
function muted(node) {
  return Boolean(
    (props.filterIds && !props.filterIds.has(node.id)) || (chain.value && !chain.value.nodeIds.has(node.id)),
  )
}
function edgeMuted(edge) {
  return Boolean(
    (props.filterIds && !(props.filterIds.has(edge.source) && props.filterIds.has(edge.target))) ||
    (chain.value && !chain.value.edgeIds.has(edge.id)),
  )
}
const annotations = computed(() => {
  if (!chain.value || !layout.value) return []
  const { x, y, k } = camera.value
  const nodes = [...visibleNodes.value, ...visibleUnits.value]
  const bounds = (n) => ({
    x: x + (n.x - n.width / 2) * k,
    y: y + (n.y - n.height / 2) * k,
    width: n.width * k,
    height: n.height * k,
  })
  const rectangles = nodes.map((n) => ({
    ...bounds(n),
    height: n.open ? 24 * k : n.height * k,
    weight: muted(n) ? 0.08 : 1,
  }))
  const labels = nodes
    .filter((n) => chain.value.nodeIds.has(n.id) && !muted(n) && n.display_label?.trim())
    .sort((a, b) => Number(b.id === props.selection?.id) - Number(a.id === props.selection?.id))
    .map((n) => ({
      id: n.id,
      kind: n.paragraphId ? 'unit' : 'paragraph',
      text: n.display_label,
      bounds: bounds(n),
      anchor: { x: x + n.x * k, y: y + n.y * k },
    }))
  visibleEdges.value
    .filter((e) => chain.value.edgeIds.has(e.id) && !edgeMuted(e))
    .forEach((e) =>
      labels.push({
        id: e.id,
        kind: 'relation',
        text: schemaText(linkTypes.value[e.type], 'name', 'link'),
        anchor: { x: x + e.midpoint.x * k, y: y + e.midpoint.y * k },
        normal: e.normal,
      }),
    )
  return placeLabels(labels, rectangles, viewport.value)
})
function choose(kind, id) {
  if (dragged) {
    dragged = false
    return
  }
  emit('select', { kind, id })
}
function clearFocus(event) {
  if (event.defaultPrevented || event.target.closest('.graph-node, .graph-edge, .chain-annotation')) return
  if (dragged) {
    dragged = false
    return
  }
  emit('select', null)
}
function rebuild(fitView = false) {
  try {
    layout.value = layoutDocument(props.document, expanded.value, positions, props.schemas)
    error.value = ''
    if (fitView) nextTick(fit)
  } catch (e) {
    error.value = e.message
  }
}
function fit() {
  if (!layout.value || !surface.value || !zoomBehavior || !svg.value) return
  const { width, height } = surface.value.getBoundingClientRect(),
    b = layout.value.bounds
  viewport.value = { width, height }
  if (!width || !height) return
  const scale = Math.max(0.12, Math.min(1.5, (width - 48) / b.width, (height - 54) / b.height))
  select(svg.value).call(
    zoomBehavior.transform,
    zoomIdentity
      .translate(width / 2 - (b.x + b.width / 2) * scale, height / 2 - (b.y + b.height / 2) * scale)
      .scale(scale),
  )
}
function openMatch(match) {
  const units = props.document.units.filter((unit) => match.nodeIds.includes(unit.id))
  const next = new Set(expanded.value)
  units.forEach((unit) => next.add(unit.paragraphId))
  const changed = next.size !== expanded.value.size
  expanded.value = next
  viewMode.value = 'graph'
  const id = match.nodeIds[0]
  emit('select', {
    id,
    kind: props.document.units.some((unit) => unit.id === id) ? 'unit' : 'paragraph',
    origin: 'motif',
    ...match,
  })
  if (changed) nextTick(() => rebuild(true))
}
function changeZoom(value) {
  select(svg.value).call(zoomBehavior.scaleBy, value)
}
function toggle(node) {
  if (dragged) {
    dragged = false
    return
  }
  const value = new Set(expanded.value)
  if (value.has(node.id)) value.delete(node.id)
  else value.add(node.id)
  expanded.value = value
  rebuild(true)
}
function openAll() {
  expanded.value = new Set(props.document.paragraphs.filter((p) => p.kind === 'paragraph').map((p) => p.id))
  rebuild(true)
}
function toggleAll() {
  if (allExpanded.value) {
    expanded.value = new Set()
    rebuild(true)
  } else {
    openAll()
  }
}
function startDrag(event, node) {
  if (event.button !== 0) return
  dragged = false
  dragState = { id: node.id, startX: event.clientX, startY: event.clientY, x: node.x, y: node.y }
  event.target.setPointerCapture(event.pointerId)
}
function moveDrag(event) {
  if (!dragState) return
  const dx = (event.clientX - dragState.startX) / (zoomLevel.value / 100),
    dy = (event.clientY - dragState.startY) / (zoomLevel.value / 100)
  if (Math.hypot(dx, dy) < 6) return
  dragged = true
  const node = layout.value.nodes.find((n) => n.id === dragState.id)
  const oldX = node.x,
    oldY = node.y
  node.x = dragState.x + dx
  node.y = dragState.y + dy
  layout.value.units
    .filter((u) => u.paragraphId === node.id)
    .forEach((u) => {
      u.x += node.x - oldX
      u.y += node.y - oldY
    })
}
function endDrag() {
  if (dragState && dragged) {
    const node = layout.value.nodes.find((n) => n.id === dragState.id)
    try {
      positions = Object.fromEntries(layout.value.nodes.map((n) => [n.id, { x: n.localX, y: n.localY }]))
      positions[node.id] = { x: node.localX + node.x - dragState.x, y: node.localY + node.y - dragState.y }
      layout.value = layoutDocument(props.document, expanded.value, positions, props.schemas)
      nextTick(fit)
    } catch (e) {
      error.value = e.message
    }
  }
  dragState = null
}
watch(
  () => props.document,
  (doc, previous) => {
    if (doc.id !== previous?.id) {
      expanded.value = new Set()
      positions = {}
    }
    rebuild(true)
  },
)
watch(
  () => [props.document.units, props.document.edges],
  () => rebuild(true),
  { deep: true },
)
watch(
  () => [props.document.paragraphs.map((p) => p.type), props.schemas.map((s) => [s.id, s.english, s.name])],
  () => rebuild(),
)
watch(
  () => props.selection,
  (value) => {
    if (value?.kind !== 'unit') return
    const unit = props.document.units.find((u) => u.id === value.id)
    if (unit && !expanded.value.has(unit.paragraphId)) {
      expanded.value = new Set([...expanded.value, unit.paragraphId])
      rebuild(true)
    }
  },
  { deep: true },
)
onMounted(() => {
  zoomBehavior = zoom()
    .scaleExtent([0.08, 5])
    .filter(
      (event) =>
        !event.target.closest('.graph-node, .graph-edge, .chain-annotation') &&
        (!event.button || event.type === 'wheel'),
    )
    .on('zoom', (event) => {
      camera.value = { x: event.transform.x, y: event.transform.y, k: event.transform.k }
      transform.value = event.transform.toString()
      zoomLevel.value = Math.round(event.transform.k * 100)
    })
  select(svg.value).call(zoomBehavior).on('dblclick.zoom', null)
  observer = new ResizeObserver(() => {
    const { width, height } = surface.value.getBoundingClientRect()
    if (width && height && (width !== viewport.value.width || height !== viewport.value.height)) fit()
  })
  observer.observe(surface.value)
  rebuild(true)
})
onUnmounted(() => {
  observer?.disconnect()
  if (svg.value) select(svg.value).on('.zoom', null)
})
function openParagraph(id) {
  expanded.value = new Set([...expanded.value, id])
  rebuild(true)
}
watch(
  () => props.analysisFocus,
  (focus) => {
    if (!focus) return
    expanded.value = new Set([
      ...expanded.value,
      ...props.document.units.filter((u) => focus.nodeIds.includes(u.id)).map((u) => u.paragraphId),
    ])
    rebuild(true)
  },
)
defineExpose({ fit, toggleAll, openParagraph, openAll })
</script>
<template>
  <section class="graph-panel pane">
    <header class="pane-header graph-header">
      <div class="graph-heading">
        <button
          v-if="collapsed"
          class="icon-button"
          :title="t('展开交互面板')"
          :aria-label="t('展开交互面板')"
          @click="emit('toggle-panel')"
        >
          <AppIcon name="PanelLeftOpen" />
        </button>
        <h2>
          {{ t('论证图') }} <span class="heading-detail">{{ t('论证地图') }}</span>
        </h2>
      </div>
      <div class="graph-tools">
        <div class="graph-view-switch" :aria-label="t('切换图视图')">
          <button
            :class="{ active: viewMode === 'graph' }"
            :aria-pressed="viewMode === 'graph'"
            @click="viewMode = 'graph'"
          >
            <AppIcon name="Network" :size="13" />{{ t('图视图') }}
          </button>
          <button
            :class="{ active: viewMode === 'gallery' }"
            :aria-pressed="viewMode === 'gallery'"
            @click="viewMode = 'gallery'"
          >
            <AppIcon name="Grid2X2" :size="13" />{{ t('图集视图') }}
          </button>
        </div>
        <template v-if="viewMode === 'graph'">
          <button
            class="icon-button"
            :title="t('添加关系')"
            :aria-label="t('添加关系')"
            @click="emit('add-relation')"
          >
            <AppIcon name="Link" /></button
          ><button
            class="graph-expand-button"
            :title="allExpanded ? t('全部折叠') : t('全部展开')"
            :aria-label="allExpanded ? t('全部折叠') : t('全部展开')"
            :aria-pressed="allExpanded"
            @click="toggleAll"
          >
            <AppIcon :name="allExpanded ? 'Minimize2' : 'Maximize2'" :size="13" />
            {{ allExpanded ? t('折叠为段落') : t('展开为 Unit') }}</button
          ><span class="tool-divider" /><button
            class="icon-button"
            :title="t('适应画布')"
            :aria-label="t('适应画布')"
            @click="fit"
          >
            <AppIcon name="Scan" />
          </button>
        </template>
      </div>
    </header>
    <slot name="focus" />
    <MotifGallery
      v-if="viewMode === 'gallery'"
      :document="document"
      :matching-document="filteredGraph"
      :motifs="motifs"
      :schemas="schemas"
      :links="links"
      :filter-ids="facetFilterIds"
      :stripe-ids="stripeIds"
      @choose="openMatch"
    />
    <div v-show="viewMode === 'graph'" class="graph-summary">
      <span><i class="status-dot" />{{ countLabel(document.chapters.length, '章节') }}</span
      ><span>{{ countLabel(document.paragraphs.filter((p) => p.kind === 'paragraph').length, '段落') }}</span
      ><span>{{ countLabel(document.units.length, '论证单元') }}</span
      ><span>{{ countLabel(visibleEdges.length, '条关系') }}</span>
    </div>
    <div v-show="viewMode === 'graph'" ref="surface" class="graph-surface">
      <svg
        ref="svg"
        class="graph-svg"
        :aria-label="t('历史论证图')"
        @pointermove="moveDrag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @click="clearFocus"
      >
        <defs>
          <FacetHatchPattern id="facet-hatch" :scale="1 / camera.k" />
          <marker
            v-for="link in links"
            :id="`arrow-${link.id}`"
            :key="link.id"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M0 1 L9 5 L0 9" fill="none" :stroke="link.color" stroke-width="1.5" />
          </marker>
        </defs>
        <g class="chain-annotations" pointer-events="none">
          <text
            v-for="label in annotations"
            :key="label.id"
            class="chain-annotation chain-note"
            :data-annotation-id="label.id"
            :data-kind="label.kind"
            :x="label.x"
            :y="label.y + 12"
          >
            <tspan v-for="(line, index) in label.lines" :key="index" :x="label.x" :dy="index ? 16 : 0">
              {{ line }}
            </tspan>
          </text>
        </g>
        <g v-if="layout" :transform="transform">
          <g
            v-for="bubble in layout.bubbles"
            :key="bubble.id"
            class="chapter-bubble"
            :data-chapter-id="bubble.id"
          >
            <path
              :d="`M${bubble.polygon.map((p) => p.join(',')).join(' L')} Z`"
              fill="none"
              stroke="#bcc2c8"
              stroke-width="1.35"
            />
            <text
              :x="bubble.titleX"
              :y="bubble.titleY + 4"
              text-anchor="middle"
              fill="#7c848e"
              font-size="11"
              font-weight="600"
            >
              {{ String(bubble.number).padStart(2, '0') }}
              <title>{{ chapterTitle(bubble, document) }}</title>
            </text>
          </g>
          <g
            v-for="node in visibleNodes"
            :key="node.id"
            class="graph-node paragraph-node"
            :class="{
              'is-selected': selection?.id === node.id,
              'graph-muted': muted(node),
              'is-faceted': stripeIds?.has(node.id),
              'is-related': chain?.nodeIds.has(node.id),
            }"
            :data-node-id="node.id"
            :opacity="muted(node) ? 0.2 : 1"
            :transform="`translate(${node.x - node.width / 2},${node.y - node.height / 2})`"
            tabindex="0"
            role="button"
            :aria-label="t('{0} 段落节点', { 0: node.id })"
            @pointerdown.stop="startDrag($event, node)"
            @click.stop="choose('paragraph', node.id)"
            @keydown.enter.stop.prevent="choose('paragraph', node.id)"
            @keydown.space.stop.prevent="choose('paragraph', node.id)"
            @dblclick.stop="emit('annotate', { kind: 'paragraph', id: node.id })"
          >
            <title>{{ paragraphSummary(node) }}</title>
            <rect
              :width="node.width"
              :height="node.height"
              rx="2"
              :fill="node.open ? (colors[node.type] || '#bcc2c8') + '1a' : '#ffffff'"
              :stroke="colors[node.type] || '#bcc2c8'"
              :stroke-width="
                selection?.id === node.id ? 3 : chain?.nodeIds.has(node.id) ? 2.2 : node.open ? 1 : 1.5
              "
            />
            <rect
              v-if="stripeIds?.has(node.id)"
              class="facet-hatch"
              opacity="0.1"
              :width="node.width"
              :height="node.height"
              rx="2"
              fill="url(#facet-hatch)"
              pointer-events="none"
            />
            <rect v-if="!node.open" width="3" :height="node.height" :fill="colors[node.type] || '#bcc2c8'" />
            <text
              :x="node.open ? 10 : 13"
              :y="node.open ? 18 : 23"
              :font-size="node.open ? 10 : 14"
              fill="#4f5969"
              font-weight="650"
            >
              {{ node.label }}
            </text>
            <g
              class="paragraph-toggle"
              :transform="`translate(${node.width - 22},3)`"
              role="button"
              tabindex="0"
              :aria-expanded="node.open"
              :aria-label="`${node.open ? t('折叠') : t('展开')} ${node.id}`"
              @pointerdown.stop
              @click.stop="toggle(node)"
              @dblclick.stop
              @keydown.enter.stop.prevent="toggle(node)"
              @keydown.space.stop.prevent="toggle(node)"
            >
              <title>
                {{ node.open ? t('折叠') : t('展开') }} · {{ countLabel(node.units.length, '个论证单元') }}
              </title>
              <rect width="20" height="20" rx="3" fill="transparent" />
              <path d="M6 10 H14" stroke="#87919c" stroke-width="1.4" />
              <path v-if="!node.open" d="M10 6 V14" stroke="#87919c" stroke-width="1.4" />
            </g>
          </g>
          <g class="graph-edges">
            <g
              v-for="edge in visibleEdges"
              :key="edge.id"
              class="graph-edge"
              :class="{
                'graph-muted': edgeMuted(edge),
                'is-related': chain?.edgeIds.has(edge.id),
                'is-selected': selection?.id === edge.id,
              }"
              :data-edge-id="edge.id"
              :data-level="edge.level"
              :opacity="edgeMuted(edge) ? 0.2 : chain ? 1 : 0.6"
              tabindex="0"
              role="button"
              :aria-label="
                t('{0} {1}关系', { 0: edge.id, 1: edge.level === 'paragraph' ? t('段落') : t('论证单元') })
              "
              @click.stop="choose('relation', edge.id)"
              @keydown.enter.stop.prevent="choose('relation', edge.id)"
              @keydown.space.stop.prevent="choose('relation', edge.id)"
              @dblclick.stop="emit('annotate', { kind: 'relation', id: edge.id })"
            >
              <path
                :d="edge.path"
                fill="none"
                :stroke="linkTypes[edge.type]?.color"
                :stroke-width="chain?.edgeIds.has(edge.id) ? (selection?.id === edge.id ? 3 : 2.2) : 1.4"
                :stroke-dasharray="
                  linkTypes[edge.type]?.dash === 'dashed'
                    ? '7 5'
                    : linkTypes[edge.type]?.dash === 'dotted'
                      ? '2 4'
                      : undefined
                "
                :marker-end="`url(#arrow-${edge.type})`"
              />
              <path
                :d="edge.path"
                fill="none"
                stroke="transparent"
                stroke-width="14"
                vector-effect="non-scaling-stroke"
                class="edge-hit"
              >
                <title>
                  {{ edge.source }} → {{ edge.target }} ·
                  {{ schemaText(linkTypes[edge.type], 'name', 'link') }}
                </title>
              </path>
            </g>
          </g>
          <g
            v-for="unit in visibleUnits"
            :key="unit.id"
            class="graph-node unit-node"
            :class="{
              'graph-muted': muted(unit),
              'is-faceted': stripeIds?.has(unit.id),
              'is-related': chain?.nodeIds.has(unit.id),
              'is-selected': selection?.id === unit.id,
              'is-focus': focusId === unit.id,
            }"
            :data-node-id="unit.id"
            :transform="`translate(${unit.x - unit.width / 2},${unit.y - unit.height / 2})`"
            :opacity="muted(unit) ? 0.2 : 1"
            tabindex="0"
            role="button"
            :aria-label="t('{0} 论证单元', { 0: unit.id })"
            @click.stop="choose('unit', unit.id)"
            @keydown.enter.stop.prevent="choose('unit', unit.id)"
            @keydown.space.stop.prevent="choose('unit', unit.id)"
            @dblclick.stop="emit('annotate', { kind: 'unit', id: unit.id })"
          >
            <title>{{ unitText(unit, document) }}</title>
            <rect
              :width="unit.width"
              :height="unit.height"
              rx="2"
              fill="#ffffff"
              :stroke="focusId === unit.id ? '#2d67b1' : colors[unit.type] || '#bcc2c8'"
              :stroke-width="focusId === unit.id ? 3 : selection?.id === unit.id ? 2 : 1.5"
            />
            <rect
              v-if="stripeIds?.has(unit.id)"
              class="facet-hatch"
              opacity="0.2"
              :width="unit.width"
              :height="unit.height"
              rx="2"
              fill="url(#facet-hatch)"
              pointer-events="none"
            />
            <rect width="3" :height="unit.height" :fill="colors[unit.type] || '#bcc2c8'" />
            <text
              :x="unit.width / 2"
              y="17"
              text-anchor="middle"
              font-size="11"
              fill="#3c4655"
              font-weight="600"
            >
              {{ unit.label }}
            </text>
            <g
              class="unit-focus-control"
              :class="{ pinned: focusId === unit.id }"
              :transform="`translate(${unit.width - 8},-12)`"
              role="button"
              :tabindex="selection?.id === unit.id || focusId === unit.id ? 0 : -1"
              :aria-label="t(focusId === unit.id ? '取消焦点' : '设为焦点')"
              :aria-pressed="focusId === unit.id"
              :aria-disabled="busy"
              @pointerdown.stop
              @dblclick.stop
              @click.stop="!busy && emit('focus', focusId === unit.id ? null : unit.id)"
              @keydown.enter.stop.prevent="!busy && emit('focus', focusId === unit.id ? null : unit.id)"
              @keydown.space.stop.prevent="!busy && emit('focus', focusId === unit.id ? null : unit.id)"
            >
              <title>{{ t(focusId === unit.id ? '取消焦点' : '设为焦点') }}</title>
              <circle cx="10" cy="10" r="12" fill="#fff" stroke="#e6dfef" />
              <AppIcon name="Sparkles" :size="20" />
            </g>
          </g>
        </g>
      </svg>
      <div v-if="error" class="graph-error" role="alert">
        {{ message(error) }}<button @click="rebuild(true)">{{ t('重试') }}</button>
      </div>
      <div v-if="!document.paragraphs.some((p) => p.kind === 'paragraph')" class="graph-empty">
        {{ t('暂无论证段落') }}
      </div>
      <div class="graph-zoom">
        <button class="icon-button" :title="t('缩小')" :aria-label="t('缩小')" @click="changeZoom(0.8)">
          <AppIcon name="Minus" /></button
        ><output>{{ zoomLevel }}%</output
        ><button class="icon-button" :title="t('放大')" :aria-label="t('放大')" @click="changeZoom(1.25)">
          <AppIcon name="Plus" />
        </button>
      </div>
      <div class="graph-legend">
        <span v-for="type in schemas" :key="type.id"
          ><i :style="{ background: type.color }" />{{ schemaText(type) }}</span
        >
      </div>
    </div>
    <footer v-show="viewMode === 'graph'" class="pane-footer graph-footer">
      <div class="graph-relation-toggles">
        <label><input v-model="showParagraphs" type="checkbox" />{{ t('段落关系') }}</label
        ><label><input v-model="showUnits" type="checkbox" />{{ t('单元关系') }}</label>
      </div>
      <NarrativePlayer
        :document="document"
        :selection="selection"
        :busy="busy || viewMode !== 'graph'"
        @select="emit('select', $event)"
      />
    </footer>
  </section>
</template>
